import { gsap } from "./vendor/gsap.js";
import { whenDispatchTailIdle } from "./state.js";

/**
 * Animation utilities for Spielgefühl: the card-winner flash, frame/wait timing
 * helpers, the scene-settled gate, and the cross-cutting GSAP seams
 * (reduced-motion gate, tween-kill cleanup) shared across mutation paths.
 */

/**
 * Flash a suit-colored glow on a card (trick winner highlight): the shadow
 * blooms to a halo then recedes to nothing, clearing the inline shadow on
 * settle so the rested card stays render()-equal. Self-gates reduced motion —
 * a winner glow is pure decoration, so it's skipped entirely (returns an empty
 * timeline that resolves immediately) rather than collapsed to an instant.
 * @param {HTMLElement} card - card element
 * @param {string} suitColor - CSS color value
 * @param {number} duration - total flash duration in ms
 * @returns {gsap.core.Timeline} the (already playing) glow timeline; awaitable
 */
export function flashCard(card, suitColor, duration = 400) {
  if (!shouldAnimateReveal()) return gsap.timeline();
  const half = duration / 2 / 1000;
  // play-test: 24px glow radius — large enough for a visible halo, not so large it bleeds into adjacent cards
  return gsap
    .timeline({ onComplete: () => (card.style.boxShadow = "") })
    .fromTo(
      card,
      { boxShadow: `0 0 0px ${suitColor}` },
      {
        boxShadow: `0 0 24px ${suitColor}`,
        duration: half,
        ease: "power1.out",
      },
    )
    .to(card, {
      boxShadow: `0 0 0px ${suitColor}`,
      duration: half,
      ease: "power1.in",
    });
}

/**
 * Wait for a specified duration, resolving on the next animation frame
 * after the target time. Uses requestAnimationFrame for frame-aligned
 * timing — eliminates setTimeout jitter that causes animations to skip
 * frames or stutter when chained.
 * @param {number} ms
 * @returns {Promise}
 */
export function wait(ms) {
  return new Promise((resolve) => {
    const start = performance.now();
    function check(now) {
      if (now - start >= ms) resolve();
      else requestAnimationFrame(check);
    }
    requestAnimationFrame(check);
  });
}

/**
 * Yield one animation frame. Use after `await wait(N)` and before
 * detaching an element (or clearing its inline transition) so the browser
 * dispatches `transitionend` on the still-live target. Without this yield
 * the event lands on a detached element and the render-debug WeakMap
 * loses the recording — visually fine, but pollutes the transition log
 * and makes future glitch-cataloguing harder to read.
 */
export function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

// --- Cross-cutting GSAP seams ---
// Shared by every mutation path (not trick-specific), so they live with the low-level
// primitives rather than in any one phase's motion module.

// Animate the reveal/fly unless GSAP is unavailable (jsdom/no-gsap degradation) or the
// user prefers reduced motion. ONE predicate gates the whole timeline; both falsy branches
// fall through to the same instant end state. The shared reduced-motion / no-GSAP gate —
// consumed by the CardPlayed flying-flip reveal (trick-ai-flight.js), the TrickResolved
// fly-out (trick-resolve.js), the exchange/declaration mutations, and the hint affordance.
export function shouldAnimateReveal() {
  if (gsap?.timeline == null) return false;
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  ) {
    return false;
  }
  return true;
}

// --- The scene-settled gate ---
// "At rest" for the board, in two halves that are both needed: nothing holds the dispatch tail
// (a burst mid-mutation, a burst still queued behind other tail work, the round-start bid replay's
// chained beat — state.js#isDispatchTailBusy) AND no GSAP motion is live on the global timeline.
// The tail half alone would miss the fire-and-forget tails a resting commit spawns (the alliance
// ink-wash, resettleSkatPile), which run PAST the release; the GSAP half alone would miss work
// that is queued but has not started tweening. Consumer: hints.js#scheduleHint, so a hint can
// never paint over flying cards (learning mode's zero delay removed the 6-16s that used to hide
// this). It is deliberately NOT the on-demand hint button's gate — a click is an explicit request.
//
// The predicate is "no children at all", deliberately stricter than the design's "no ACTIVE
// children": a child that is merely queued has not started tweening yet, and starting the hint
// clock in front of it is the bug this gate exists to prevent. What that strictness costs is that
// a PARKED child reads as motion — `gsap.timeline({ paused: true })` (trick-resolve.js,
// game-start-entrance.js) and `gsap.delayedCall` (result/result-stats-band.js) are root children
// with no motion in them. Sound today because every one of those is either killed or covered by
// the tail claim before a hint is ever scheduled against it, NOT because the surface cannot
// produce one — so a future choreography that parks a paused timeline on the root and leaves it
// there would push every hint out to the cap below. That, and a `repeat: -1` tween (none exists
// on the game surface, checked 2026-09-05), are the two shapes to re-check this against.
//
// The tail half is awaited FIRST, and that await costs at least one microtask turn even when the
// tail is already free. That is load-bearing at the round-start deal: game.js#render calls
// scheduleHint one synchronous statement before renderWithAnimation goes on to build the deal's
// master timeline, so a fully synchronous predicate would read "settled" over cards that have not
// started flying yet. Evaluate the GSAP half only after an await.
//
// Same number as dispatch.js#MUTATION_TIMEOUT_MS and the same job — a wedged tween or an
// unreleased tail must never STARVE a hint, so at the cap we proceed as if settled rather than
// dropping the hint. Deliberately a separate constant, not an import: dispatch.js imports this
// module, so reading its export here would close a cycle. Note the SCOPE differs from the value's
// origin, which is why it is re-judged rather than inherited — there 8000 caps ONE mutation, here
// it caps a whole chained tail. Measured worst case on this surface: a fresh Play's deal plus the
// round-start bid replay spent 4.5s under the gate (browser, 2026-09-05), so the headroom is
// about 1.8x. A materially longer replay would trip the cap and reveal the hint mid-motion — the
// number to revisit first if that is ever reported.
const SCENE_SETTLE_TIMEOUT_MS = 8000;

export function whenSceneSettled() {
  let capTimer;
  const cap = new Promise((resolve) => {
    capTimer = setTimeout(resolve, SCENE_SETTLE_TIMEOUT_MS);
  });
  return Promise.race([pollUntilAtRest(), cap]).finally(() =>
    clearTimeout(capTimer),
  );
}

// The cap wins the race but does not stop this loop: a timeline that never empties leaves one rAF
// poll parked per schedule (an unreleased tail leaves a parked waiter and no CPU at all). Left
// deliberately — the residual is one property read per frame, and it is confined to the pathology
// the cap already covers. If the parked-child shape above ever does reach the root, though, this
// stops being hypothetical and both want fixing together: the predicate first, this second.
//
// Poll per frame rather than sleeping a fixed span: the choreographies vary from a 0.3s pulse to
// the deal's multi-second master, and the point is to start the hint clock the moment the last
// one lands. Re-await the tail inside the loop — a frame in which the GSAP work ended may be the
// same frame the next burst claims the tail.
async function pollUntilAtRest() {
  await whenDispatchTailIdle();
  while (gsap.globalTimeline.getChildren(false, true, true).length > 0) {
    await nextFrame();
    await whenDispatchTailIdle();
  }
}

// --- The skippable-settle scaffold ---
// Two orthogonal primitives shared by the settle/skip choreographies (deal-sort,
// full-table-deal, declaration-reveal, exchange, boundary-settle, outcome-board-beat):
// the kill-safe settled promise and the click-to-skip surface. Each choreography
// composes them with its own residue-shedding `onSettle`.

// The settled promise resolves on BOTH natural completion and kill. gsap's own
// then() never resolves a *killed* timeline, so an upstream `await tl.settled`
// would hang on interrupt without this hand-wiring — the one subtlety this
// scaffold exists to own. `onSettle` (residue shedding) runs on whichever path
// fires first; it is optional for parent-driven settles with nothing to shed.
// Dev choreography-scrub seam: every master timeline that wires a settle passes its `tl`
// through here, so this is the one place to hand the currently-playing master to the
// scrubber (dev/choreography-scrub.js) for window.__seek / __freeze. Null in every normal
// session — never touched in prod.
let onActiveMaster = null;
export function setActiveMasterHook(fn) {
  onActiveMaster = fn;
}

// Register tl as the active master directly, bypassing wireSettled — the lab's
// replay() seam (dev/lab-scrub.js) calls this on every replay so window.__choreo
// reaches every lab tab, not only the ones whose choreography composes wireSettled.
export function registerActiveMaster(tl) {
  onActiveMaster?.(tl);
}

export function wireSettled(tl, onSettle) {
  let resolveSettled;
  const settled = new Promise((resolve) => {
    resolveSettled = resolve;
  });
  const settle = () => {
    onSettle?.();
    resolveSettled();
  };
  tl.add(settle);
  tl.eventCallback("onInterrupt", settle);
  onActiveMaster?.(tl);
  return settled;
}

// Arm a bubble-phase, non-swallowing click on appEl that jumps the timeline to
// its end (progress(1) → the added settle callback → resolve). Non-swallowing is
// deliberate: a click on a live control (Quit) both skips and acts. Returns the
// disarm thunk — the caller drops the listener on settle (every exit path).
export function armClickSkip(tl, appEl) {
  const skip = () => tl.progress(1);
  appEl.addEventListener("click", skip);
  return () => appEl.removeEventListener("click", skip);
}

// --- Deferred prompt reveal (the "don't stall the prompt" UX) ---
// The next prompt is rendered + faded in MID-animation, at a beat the preceding
// choreography exposes, instead of waiting for the animation to settle and the
// terminal Awaiting* mutation to arm it. The dispatch loop arms a reveal thunk for
// a blocking burst (prompt-ui builds the closure over the terminal state); the
// choreography fires it at its ready-beat (revealPendingPrompt); the burst's
// finally clears any unfired arm so it can't leak into the next burst (a
// reduced-motion / non-blocking burst never fires, and the terminal mutation arms
// the prompt the ordinary way). Module-level singleton: dispatch is sequential, so
// at most one burst's reveal is ever pending.
let pendingPromptReveal = null;
export function armPromptReveal(reveal) {
  pendingPromptReveal = reveal;
}
export function revealPendingPrompt() {
  const reveal = pendingPromptReveal;
  pendingPromptReveal = null;
  reveal?.();
}
export function clearPendingPromptReveal() {
  pendingPromptReveal = null;
}

// The cleanup seam: kill any tweens on the node, then detach.
// killTweensOf is a no-op over a CSS-transition flight but documents the lifecycle boundary
// and guards a future GSAP motion. A mutation reaches for it at the point a node leaves
// addressability (docs/dom/LIFECYCLE.md). Removal is folded in because every
// caller detaches (trick ghost archival, discard removal). Shared by the trick ghost
// archival and the EXCHANGING DiscardMade mutation.
export function releaseNode(node) {
  gsap.killTweensOf(node);
  node.remove();
}

// Fade a node out, resolving when the fade completes. Reduced motion / no GSAP (or
// a null node) resolves at once without touching it — the caller is mid-teardown,
// so an instant cut is the graceful degradation. Used by the Quit return transition
// (api.js#showGameStart) to fade the board out before the start screen renders.
export function fadeNodeOut(node, seconds) {
  if (node == null || !shouldAnimateReveal()) return Promise.resolve();
  return new Promise((resolve) =>
    gsap.to(node, {
      autoAlpha: 0,
      duration: seconds,
      ease: "power1.in",
      onComplete: resolve,
    }),
  );
}
